<script lang="ts" setup>
    import '../../../css/choice.css';
    import {onMounted,computed,reactive,ref} from 'vue';  
    import ModalDialog from "../../components/ModalDialog.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import {ButtonDisplaySize,ButtonDisplayStyle,DisplayStyle,ModalSize} from '../../Enums/UiEnums';
    import {ActionButtonStyle} from '../../lib/Types/Types';      
    import {ErrorReturnMessage,getPrintDetails} from '../../lib/Api/PaymentPlan';   
    import {getCompanyLogo} from '../../lib/Api/Company';     
    import {ICompanyInformation,IPatientPlanDetails,IPatientInstallmentDetails} from '../../lib/Models/PaymentPlanModel'; 
    import {popAlert,ModalStyles} from '../../lib/Notification';   
    import TitledRow from "../../components/TitledRow.vue";
    import {prettyCurrency} from '../../lib/Formatting/Number'; 
    import * as Printing from '../../lib/Printing'

    const defaultLogo = computed<string>(() => {
        if (formData.imgSrc === "") {
            return "No Logo Found";
        }
        return "Loading...";
    });


    const emit = defineEmits<{
        (e: "onDisplay") : void
    }>();
    const DataFound = computed<boolean>(()=> {
        if (!formData.companyInformation) {
            return false;
        } 
        if (!formData.patientDetails) {
            return false;
        }  
        if (!formData.installmentDetails) {
            return false;
        } 
        return true;
    }); 
    const StatusClass = computed<string>(()=> {
        if (formData.patientDetails?.statusIssue) {
            return "text-danger fw-bold";
        } 
        return "";
    });
    const formData = reactive <
    {     
        modalDisplayPlan: boolean, 
        companyInformation?: ICompanyInformation,
        patientDetails?: IPatientPlanDetails,
        installmentDetails?: IPatientInstallmentDetails[],
        imgSrc?: string
       
    }>({     
        modalDisplayPlan: false
    });
    const props = withDefaults(defineProps<{ 
        show: boolean,
        paymentPlanId: number 
    }>(),
    { 
        show: false,
        paymentPlanId: 0 
    });

    onMounted(async () => {   
        refreshData(); 
        formData.modalDisplayPlan = props.show;
    });
        
    async function refreshData() : Promise<void>  {  
        const respGet = await getPrintDetails(props.paymentPlanId); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.printDisplay,"Payment Plan Print Details",ModalStyles.danger);
            return;
        } 
        if (respGet.success.companyData) {  
            formData.companyInformation = respGet.success.companyData;
        }  
        if (respGet.success.patientData) {  
            formData.patientDetails = respGet.success.patientData;
        }  
        if (respGet.success.installmentDetails) {  
            formData.installmentDetails = respGet.success.installmentDetails;
        } 
        const respLogo = await getCompanyLogo();
        if (!respLogo) {
            formData.imgSrc = "";
            return;
        }
        if (respLogo.mimeType && respLogo.imageBase64) {
            formData.imgSrc = `data:${respLogo.mimeType};base64,${respLogo.imageBase64}`;
            return;
        }
        formData.imgSrc = "";
    }  
    
    async function modalHiddenResponse() : Promise<void> {
        formData.modalDisplayPlan = false;
        emit("onDisplay");
    } 
    async function PrintPlan() : Promise<void> {
        const printInformation : Printing.ICopyHtmlToPrint = {
            classID: "PrintData",
            title: "Payment Plan Print Details", 
            classOverRide: ["display-installments"]
        };
        Printing.CopyHtmlToPrint(printInformation); 
    }              
</script> 
<style scoped>
    @import url('../../../css/paymentPlanGrid.css');
 
    .display-installments {
        overflow-x: auto;
        height: 10rem;
        width: 100%;
        display: block;
    }
    @media print {
        .display-installments-override {
            display: block;
        }
    }
</style>
<template>
    <ModalDialog 
        :show="formData.modalDisplayPlan"
        @hidden="modalHiddenResponse"  
        :size="ModalSize.Large"
        :closeOnClickOutside=false>
        <template #title>
            <span>Payment Plan Print Details</span> 
        </template>
        <div v-if="!DataFound">
            <div class="text-center text-danger">No Data Found</div>
        </div>
        <div v-if="DataFound" class="PrintData">
            <table v-if="formData.companyInformation" class="label-isalus-small">
                <tr>
                    <td rowspan="5" class="pe-1" style="border-right: 1px solid silver;">
                        <img v-if="formData.imgSrc" alt="" :src="formData.imgSrc" style="width: 150px;" />
                        <div v-if="!formData.imgSrc" class="fst-italic text-danger">{{defaultLogo}}</div>
                    </td>
                    <td class="ps-2">
                        {{formData.companyInformation.companyProfileName}}
                    </td>
                </tr> 
                <tr>
                    <td class="ps-2">
                        {{formData.companyInformation.streetAddress1}}
                    </td>
                </tr>  
                <tr>
                    <td class="ps-2">
                        {{formData.companyInformation.streetAddress2}}
                    </td>
                </tr>   
                <tr>
                    <td class="ps-2">
                        {{formData.companyInformation.cityStateZip}}
                    </td>
                </tr>   
                <tr>
                    <td class="ps-2">
                        {{formData.companyInformation.phoneNumber}}
                    </td>
                </tr> 
            </table>
            <hr/>  
            <TitledRow v-if="formData.patientDetails" class="mt-2" :titleWidth="12" title="Your Full Payment Plan Details"/>
            <div v-if="formData.patientDetails" class="grid-container-patient-print label-isalus-small">
                <div>Patient Name</div>
                <div>{{formData.patientDetails.patientName}}</div> 
                <div>Remaining Amount</div>
                <div>{{prettyCurrency(formData.patientDetails.remainingTotal)}}</div> 
                <div>Total Amount</div>
                <div>{{prettyCurrency(formData.patientDetails.totalAmount)}}</div> 
                <div>Remaining Installments</div>
                <div>{{formData.patientDetails.remainingInstallments}}</div> 
                <div>Amount Paid</div>
                <div>{{prettyCurrency(formData.patientDetails.totalPayments)}}</div> 
                <div>Current Status</div>
                <div :class="StatusClass">{{formData.patientDetails.statusDescription}}</div> 
                <div>Total Installments</div>
                <div>{{formData.patientDetails.totalInstallments}}</div>  
            </div>
            <hr/>    
            <TitledRow v-if="(formData.installmentDetails?.length ?? 0) > 0" class="mt-2" :titleWidth="12" title="Your Individual Installment Details"/>
            <div v-if="(formData.installmentDetails?.length ?? 0) > 0" class="row"> 
                <div class="display-installments col-6">  
                    <table style="table-layout: fixed;" class="label-isalus-small">
                        <colgroup>
                            <col style="width: 2rem"/>
                            <col style="width: 6rem"/>
                            <col style="width: 6rem"/>
                            <col style="width: 6rem"/>
                        </colgroup>
                        <tr>
                            <td class="text-start fw-bolder">#</td>
                            <td class="text-start fw-bolder">Due Date</td>
                            <td class="text-end fw-bolder">Amount</td>
                            <td class="text-end fw-bolder">Paid</td>  
                        </tr>
                        <tr v-for="(installmentRecord,index) in formData.installmentDetails" :key="`Key${installmentRecord.installmentNumber}`"> 
                            <td class="text-start">{{installmentRecord.installmentNumber}}</td>
                            <td class="text-start">{{installmentRecord.dueDate}}</td>
                            <td class="text-end">{{prettyCurrency(installmentRecord.installmentAmount)}}</td>
                            <td class="text-end">{{prettyCurrency(installmentRecord.paymentAmount)}}</td>
                        </tr>                  
                    </table> 
                </div> 
            </div> 
        </div>
        <template #footer>  
            <div v-if="DataFound">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Print" 
                    :text="'Print'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="PrintPlan"
                />
            </div>
        </template>
    </ModalDialog>    
</template>
